import React, { useState } from "react";
import styles from "./PlaydateInviteCreationView.module.css";
import Layout from "../../layout/Layout";
import ActionButton from "../../button/ActionButton";
import UnderlineButton from "../../button/UnderlineButton";
import { Connection, ParticipantSummary, PlaydateInvite, PlaydateType, ScheduleOptionType } from "../../../gql/types";
import { SelectedDayPartPair } from "../../time/types";
import TextInputField, { TextInputFieldType } from "../../input/TextInputField";
import TimeSelectionEditDayPartTypeView from "../../time/TimeSelectionEditDayPartTypeView";
import DateInput from "../../time/DateInput";
import TextAreaInput from "../../input/TextAreaInput";
import ConnectionSelectInputRow from "../../connection/ConnectionSelectInputRow";
import { DraftPlaydateInvite } from "../types";
import { convertScheduleOptions } from "../../../service/playdate/converters/converter";
import { useUser } from "../../../contexts/UserContext";
import composeIcon from "../../../assets/icons/composeIconBlack.svg";
import TimeSelectionSelectionHeader from "../../time/TimeSelectionSelectionHeader";
import { convertToISO8601 } from "../../../time/convert";
import { format, isBefore, parseISO, subDays } from "date-fns"; // top of file

type Mode = "create" | "edit";

export interface PlaydateInviteCreationViewProps {
    mode: Mode;
    onBack: () => void;
    onContinue: (invite: DraftPlaydateInvite, setLoading: (loading: boolean) => void) => void;
    baseZIndex?: number;
    invite?: PlaydateInvite;
    initialDraft?: DraftPlaydateInvite;
    initialGuests?: ParticipantSummary[];
}

const renderSectionHeader = (title: string) => (
    <div className={styles.sectionHeader}>
        <div className={styles.sectionHeaderText}>
            {title}
        </div>
        <img
            src={composeIcon}
            alt=""
            className={styles.sectionHeaderIcon}
        />
    </div>
);

const PlaydateInviteCreationView: React.FC<PlaydateInviteCreationViewProps> = ({
    mode,
    onBack,
    onContinue,
    baseZIndex,
    invite,
    initialDraft,
    initialGuests,
}) => {
    const [showErrors, setShowErrors] = useState(false);
    const [loading, setLoading] = useState(false);

    const titleRef = React.useRef<HTMLInputElement>(null);
    const locationRef = React.useRef<HTMLInputElement>(null);
    const dateInputRef = React.useRef<HTMLDivElement>(null);
    const guestRef = React.useRef<HTMLDivElement>(null);
    const scheduleRef = React.useRef<HTMLDivElement>(null);
    const scheduleInputRef = React.useRef<{ validate: () => { isValid: boolean; selected: SelectedDayPartPair[] } }>(null);

    const isEditMode = mode === "edit";

    const [title, setTitle] = useState(
        isEditMode ? invite?.title ?? "" : initialDraft?.title ?? ""
    );

    const [location, setLocation] = useState(
        isEditMode ? invite?.location ?? "" : initialDraft?.location ?? ""
    );

    const [selectedDayParts, setSelectedDayParts] = useState<SelectedDayPartPair[]>(
        isEditMode
            ? (invite ? convertScheduleOptions(invite.scheduleOptions) : [])
            : (initialDraft?.scheduleOptions ?? [])
    );

    const [rsvpDeadline, setRsvpDeadline] = useState(
        isEditMode
            ? (invite?.rsvpDeadline ? invite.rsvpDeadline.split("T")[0] : "")
            : (initialDraft?.rsvpDeadline ? initialDraft.rsvpDeadline.split("T")[0] : "")
    );

    const [details, setDetails] = useState(
        isEditMode ? invite?.details ?? "" : initialDraft?.details ?? ""
    );

    const [guests, setGuests] = useState<ParticipantSummary[]>(
        isEditMode
            ? (invite?.guests ?? initialGuests ?? [])
            : (initialDraft?.guestConnections ?? initialGuests ?? [])
    );

    const { user: loggedInUser } = useUser();

    const [isValidFields, setIsValidFields] = useState({
        title: false,
        location: false,
        scheduleOptions: false,
        rsvpDeadline: false,
        guests: false,
    });

    React.useEffect(() => {
        setIsValidFields(prev => ({
            ...prev,
            scheduleOptions: selectedDayParts.length > 0,
            rsvpDeadline: !!rsvpDeadline,
            guests: guests.length > 0,
        }));
    }, [selectedDayParts, rsvpDeadline, details, guests]);

    React.useEffect(() => {
        if (selectedDayParts.length === 0) {
            setRsvpDeadline(""); // 🧹 Clear it if all options are removed
            return;
        }

        if (rsvpDeadline) return;

        // Compute latest proposed date
        const dates = selectedDayParts.map(pair => new Date(pair.date + "T00:00:00"));
        const latest = new Date(Math.max(...dates.map(d => d.getTime())));

        // Compute 2 days before latest
        const proposedDeadline = subDays(latest, 2);

        // Get today's date (start of day)
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        const finalDeadline = isBefore(proposedDeadline, today) ? today : proposedDeadline;
        setRsvpDeadline(format(finalDeadline, "yyyy-MM-dd"));
    }, [selectedDayParts]);

    const handleContinue = () => {
        setLoading(true);

        const { isValid: isScheduleValid, selected: validatedDayParts } =
            scheduleInputRef.current?.validate?.() ?? { isValid: false, selected: [] };

        if (!Object.values(isValidFields).every(Boolean) || !isScheduleValid) {
            setShowErrors(true);

            const isScheduleValid = scheduleInputRef.current?.validate?.() ?? false;

            if (!isValidFields.title) {
                titleRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
                titleRef.current?.focus();
            } else if (!isValidFields.location) {
                locationRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
                locationRef.current?.focus();
            } else if (!isScheduleValid) {
                scheduleRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
            } else if (!isValidFields.rsvpDeadline) {
                dateInputRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
            } else if (!isValidFields.guests) {
                guestRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
            }
            setLoading(false);
            return;
        }

        const draft: DraftPlaydateInvite = {
            title,
            location,
            playdateType: invite?.playdateType ?? PlaydateType.Private,
            optionType: invite?.scheduleOptionType ?? ScheduleOptionType.OneTime,
            scheduleOptions: validatedDayParts,
            rsvpDeadline: rsvpDeadline
                ? convertToISO8601(rsvpDeadline)
                : undefined,
            details,
            guestConnections: guests,
            externalGuests: invite?.externalGuests ?? [], // default to existing or empty
            organizerID: invite?.organizer?.id ?? (loggedInUser?.id ?? ""),
        };

        onContinue(draft, setLoading);
    };

    return (
        <Layout
            navbarConfig={{
                title: "Create Invite",
                onBack: onBack,
            }}
            containerConfig={{
                removeTopPadding: true,
                noPadding: true,
            }}
            baseZIndex={baseZIndex ? baseZIndex + 1 : 1}
        >
            <div className={styles.editorContainer}>
                <div className={styles.infoSection}>
                    Please edit and confirm the details below to create your invite and share.
                    {(!initialGuests || (initialGuests && initialGuests.length == 0)) && (
                        <>
                            <br /><br />
                            <span>For a non-Plii user, you can share the invite after creation!</span>
                        </>
                    )}
                </div>
                <div className={styles.contentDivider} />
                {renderSectionHeader("Title")}
                <div className={styles.sectionInputContainer} ref={titleRef}>
                    <TextInputField
                        type={TextInputFieldType.FREEFORM}
                        placeholder="Enter title"
                        value={title}
                        onChange={setTitle}
                        onValidChange={(isValid) => {
                            setIsValidFields((prev) => ({ ...prev, title: isValid }));
                        }}
                        errorStr={showErrors && !isValidFields.title ? "Title is required." : undefined}
                    />
                </div>
                {renderSectionHeader("Location")}
                <div className={styles.sectionInputContainer} ref={locationRef}>
                    <TextInputField
                        type={TextInputFieldType.FREEFORM}
                        placeholder="Enter location"
                        value={location}
                        onChange={setLocation}
                        onValidChange={(isValid) =>
                            setIsValidFields((prev) => ({ ...prev, location: isValid }))
                        }
                        errorStr={showErrors && !isValidFields.location ? "Location is required." : undefined}
                    />
                </div>

                <div className={styles.sectionHeader}>
                    <TimeSelectionSelectionHeader
                        key={"timeSelectionHeader"}
                        header={"Proposed Dates & Times"}
                        subtitle={"Give the guests as many options as you can to make it easier to finalize the invite later."}
                        message={initialDraft?.scheduleReason}
                        popoverTitle={"Why these options?"}
                    />
                </div>
                <div className={styles.sectionInputContainer}>
                    <TimeSelectionEditDayPartTypeView
                        ref={scheduleInputRef}
                        initialSelectedDayParts={selectedDayParts}
                        onEditChange={setSelectedDayParts}
                        onExploreMore={() => console.log("Explore more clicked")}
                    />
                </div>
                <div className={styles.contentDivider} />
                {renderSectionHeader("RSVP Deadline")}
                <div className={styles.sectionInputContainer} ref={dateInputRef}>
                    <DateInput
                        date={rsvpDeadline}
                        onDateChange={setRsvpDeadline}
                        popperPlacement="bottom"
                        hasMinHeight={true}
                        noPastDate={true}
                        maxDate={
                            selectedDayParts.length > 0
                                ? new Date(
                                    Math.max(
                                        ...selectedDayParts.map(p => new Date(p.date + "T00:00:00").getTime())
                                    )
                                )
                                : undefined
                        }
                        enableError={true}
                        errorStr={showErrors && !isValidFields.rsvpDeadline ? "RSVP Deadline is required." : undefined}
                    />
                </div>
                <div className={styles.smallestDivider} />
                {renderSectionHeader("Details")}
                <div className={styles.sectionInputContainer}>
                    <TextAreaInput
                        placeholder="Add details..."
                        message={details}
                        setMessage={setDetails}
                    />
                </div>
                <div className={styles.smallerDivider} />
                {renderSectionHeader("Guests")}
                <div className={styles.sectionInputContainer} ref={guestRef}>
                    <ConnectionSelectInputRow
                        initialParticipants={guests}
                        onChange={setGuests}
                        placeholder="Add guests"
                        baseZIndex={baseZIndex}
                        errorStr={showErrors && !isValidFields.guests ? "Guest is required." : undefined}
                    />
                </div>

                <div className={styles.buttonContainer}>
                    <ActionButton
                        hasLoadingSignal={loading}
                        onClick={handleContinue}
                    >
                        {mode == "create" ? "Create and Share Invite"
                            : "Update Invite"}
                    </ActionButton>
                    <UnderlineButton
                        onClick={onBack}
                    >
                        Back
                    </UnderlineButton>
                </div>
            </div>
        </Layout >
    );
};

export default PlaydateInviteCreationView;
